import createClient, { type Client, type Middleware } from 'openapi-fetch';

import {
  isStationScoped,
  STATION_HEADER,
  stationSelection,
  type StationSelection,
} from '../features/stationPortal/stationSelection';
import { readProblem, toApiError } from './problem';
import type { paths } from './schema';
import { sessionStore, type Session, type SessionStore, type TokenResponse } from './session';

export const REFRESH_PATH = '/api/v1/auth/refresh';
/** Código del problem+json que dispara la renovación del access token. */
export const TOKEN_EXPIRED = 'TOKEN_EXPIRED';
/** Sesión revocada, usuario inexistente o rol distinto: el access token ya no sirve aunque no haya vencido. */
export const TOKEN_INVALID = 'TOKEN_INVALID';

type FetchLike = (input: Request) => Promise<Response>;

/** Lee `globalThis.fetch` en cada llamada (MSW y otros interceptores lo reemplazan después de cargar). */
const globalFetch: FetchLike = (input) => globalThis.fetch(input);

export interface AuthOptions {
  /** Origen del API (en el navegador, el mismo de la app: nginx y Vite hacen de proxy). */
  baseUrl: string;
  store?: SessionStore;
  fetch?: FetchLike;
}

export interface AuthMiddleware {
  middleware: Middleware;
  /**
   * Renueva la sesión con la cookie de refresh. Si ya hay una renovación en curso, devuelve la
   * misma promesa (nunca dos a la vez). Si falla, limpia la sesión y resuelve `null`.
   */
  refresh: () => Promise<Session | null>;
}

/**
 * Middleware de sesión para openapi-fetch:
 * 1. agrega `Authorization: Bearer <access>` desde la sesión en memoria;
 * 2. ante un 401 con `code: TOKEN_EXPIRED` (o `TOKEN_INVALID` si la petición llevaba token), hace UNA
 *    renovación en vuelo (`POST /api/v1/auth/refresh`, `credentials: 'include'`, `X-Auth-Mode: cookie`),
 *    compartida por todas las respuestas 401 concurrentes, y reintenta la petición original una sola vez;
 * 3. si la renovación falla, limpia la sesión y devuelve el 401 original: la UI vuelve a visitante y
 *    ofrece ingresar de nuevo. Si el API la rechazó (401/403) además se avisa con `store.expire()`
 *    para que la app abra el ingreso.
 */
export function createAuthMiddleware({ baseUrl, store = sessionStore, fetch = globalFetch }: AuthOptions): AuthMiddleware {
  let inFlight: Promise<Session | null> | null = null;
  /** Copia intacta de cada petición (el cuerpo solo se puede leer una vez) y el token con que salió. */
  const pending = new Map<string, { retry: Request; token: string | null }>();

  const renew = async (): Promise<Session | null> => {
    try {
      const response = await fetch(
        new Request(new URL(REFRESH_PATH, baseUrl), {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Auth-Mode': 'cookie', Accept: 'application/json' },
        }),
      );
      if (!response.ok) {
        // 401/403: el API rechazó la sesión (caducó o se revocó). Un 5xx no dice nada de ella.
        if (response.status === 401 || response.status === 403) store.expire();
        else store.clear();
        return null;
      }
      const body = (await response.json()) as Partial<TokenResponse> | null;
      if (!body || typeof body.access_token !== 'string' || typeof body.expires_in !== 'number' || !body.user) {
        store.clear();
        return null;
      }
      return store.setFromTokenResponse(body as TokenResponse);
    } catch {
      store.clear();
      return null;
    }
  };

  const refresh = (): Promise<Session | null> => {
    inFlight ??= renew().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  const middleware: Middleware = {
    onRequest({ request, id }) {
      const token = store.getAccessToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      pending.set(id, { retry: request.clone(), token });
      return request;
    },

    async onResponse({ response, id }) {
      const entry = pending.get(id);
      pending.delete(id);
      if (response.status !== 401 || !entry) return response;

      const problem = await readProblem(response);
      // Sin token en la petición (visitante o sesión sin conexión) un TOKEN_INVALID no es de la sesión.
      const rejected = problem?.code === TOKEN_EXPIRED || (problem?.code === TOKEN_INVALID && entry.token !== null);
      if (!rejected) return response;

      // Si otra petición ya renovó mientras esta viajaba, basta con reintentar con el token nuevo.
      const current = store.getAccessToken();
      const token = current && current !== entry.token ? current : ((await refresh())?.accessToken ?? null);
      if (!token) return response;

      entry.retry.headers.set('Authorization', `Bearer ${token}`);
      // El reintento no vuelve a pasar por el middleware: a lo sumo un reintento por petición.
      return fetch(entry.retry);
    },

    onError({ id }) {
      pending.delete(id);
    },
  };

  return { middleware, refresh };
}

/**
 * `X-Station-Id` con la estación elegida en el selector del panel, solo en las rutas del portal de
 * estación (`/api/v1/station/*` y `/api/v1/media/*`) y solo para la persona que la eligió. Sin
 * elección (una sola membresía) no agrega nada y el API usa la estación del evento actual.
 */
export function createStationMiddleware(
  selection: StationSelection = stationSelection,
  store: SessionStore = sessionStore,
): Middleware {
  return {
    onRequest({ request }) {
      if (request.headers.has(STATION_HEADER)) return request;
      const stationId = selection.stationFor(store.get()?.user.id);
      if (!stationId || !isStationScoped(new URL(request.url).pathname)) return request;
      request.headers.set(STATION_HEADER, stationId);
      return request;
    },
  };
}

export interface ApiClient<Paths extends {} = paths> {
  client: Client<Paths>;
  refresh: () => Promise<Session | null>;
}

export interface ApiClientOptions extends AuthOptions {
  /** Estación elegida en el panel (por defecto, la de la app). */
  stationSelection?: StationSelection;
}

/** Cliente openapi-fetch con el middleware de sesión. `Paths` son los tipos generados del contrato. */
export function createApiClient<Paths extends {} = paths>(options: ApiClientOptions): ApiClient<Paths> {
  const fetch = options.fetch ?? globalFetch;
  const client = createClient<Paths>({ baseUrl: options.baseUrl, fetch, credentials: 'same-origin' });
  const auth = createAuthMiddleware({ ...options, fetch });
  // Primero la estación: el middleware de sesión guarda una copia de la petición para reintentarla
  // tras renovar el token, y esa copia también debe llevar el header.
  client.use(createStationMiddleware(options.stationSelection, options.store));
  client.use(auth.middleware);
  return { client, refresh: auth.refresh };
}

function defaultBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (configured) return configured;
  return typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:8000';
}

const app = createApiClient({ baseUrl: defaultBaseUrl() });

/** Cliente tipado del API (tipos generados de `docs/openapi.json` con `pnpm gen:api`). */
export const api = app.client;

/** Renovación silenciosa (p. ej. al abrir la app, para recuperar la sesión desde la cookie). */
export const refreshSession = app.refresh;

/**
 * Desenvuelve el resultado de openapi-fetch: devuelve `data` o lanza `ApiError` (RFC 9457).
 * Uso: `queryFn: () => unwrap(api.GET('/api/v1/stations/map'))`.
 */
export async function unwrap<Data>(
  request: Promise<{ data?: Data; error?: unknown; response: Response }>,
): Promise<Data> {
  const { data, error, response } = await request;
  if (error !== undefined || !response.ok) throw toApiError(response, error);
  return data as Data;
}
